import React, { useMemo, useState } from 'react';
import { array, bool, string } from 'prop-types';
import css from './SeoDiscovery.module.css';

const startOfMonth = date => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1);

const dateKey = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateFromKey = key => {
  const match = String(key || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const eventDate = ev => {
  const keyedDate = dateFromKey(ev.eventDateKey);
  if (keyedDate) {
    return keyedDate;
  }
  const parsed = new Date(ev.eventDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const eventDateKey = ev => {
  if (ev.eventDateKey) {
    return ev.eventDateKey;
  }
  const date = eventDate(ev);
  return date ? dateKey(date) : null;
};

const eventTimeLabel = ev => {
  const match = String(ev.eventDateDisplay || '').match(/\b(\d{1,2}:\d{2}\s?[AP]M)\b/i);
  return match ? match[1].toUpperCase().replace(/\s+/, ' ') : null;
};

/** Compact calendar cell copy: start time + venue (not the long listing title). */
export const eventCalendarCellLabel = ev => {
  const time = eventTimeLabel(ev);
  const venue = String(ev?.venueName || '').trim();
  if (time && venue) {
    return `${time} · ${venue}`;
  }
  if (time) {
    return time;
  }
  if (venue) {
    return venue;
  }
  return String(ev?.title || 'Event').trim() || 'Event';
};

const eventMonth = events => {
  const first = (events || []).map(eventDate).find(Boolean);
  return startOfMonth(first || new Date());
};

const monthLabel = date =>
  date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

/** Mobile list label: "August 26". Desktop keeps the bare day number. */
export const eventCalendarDayLabel = (day, { includeMonth = false } = {}) => {
  if (!(day instanceof Date) || Number.isNaN(day.getTime())) {
    return '';
  }
  if (!includeMonth) {
    return String(day.getDate());
  }
  return day.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
};

const calendarDays = month => {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  return Array.from({ length: 42 }).map((_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return day;
  });
};

const eventsByDay = events =>
  (events || []).reduce((map, ev) => {
    const key = eventDateKey(ev);
    if (!key) {
      return map;
    }
    return {
      ...map,
      [key]: [...(map[key] || []), ev],
    };
  }, {});

/** Shared page-anchor target for calendar “+N more” → full upcoming-events list. */
export const ALL_UPCOMING_EVENTS_ANCHOR_ID = 'all-upcoming-events';

const SeoEventCalendar = props => {
  const { events, venueName, showImages = true, moreEventsHref = null } = props;
  const initialMonth = useMemo(() => eventMonth(events), [events]);
  const [month, setMonth] = useState(initialMonth);
  const days = calendarDays(month);
  const grouped = useMemo(() => eventsByDay(events), [events]);

  if (!events?.length) {
    return (
      <section className={css.section}>
        <h2>Event Calendar</h2>
        <p>There are no upcoming events listed for {venueName} right now.</p>
      </section>
    );
  }

  return (
    <section className={css.section}>
      <div className={css.calendarHeader}>
        <h2>Event Calendar</h2>
        <div className={css.calendarControls}>
          <button type="button" onClick={() => setMonth(addMonths(month, -1))}>
            Previous
          </button>
          <strong>{monthLabel(month)}</strong>
          <button type="button" onClick={() => setMonth(addMonths(month, 1))}>
            Next
          </button>
        </div>
      </div>
      <div className={css.calendarGrid} aria-label={`${venueName} event calendar`}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className={css.calendarWeekday}>
            {day}
          </div>
        ))}
        {days.map(day => {
          const key = dateKey(day);
          const dayEvents = grouped[key] || [];
          const outsideMonth = day.getMonth() !== month.getMonth();
          const isEmpty = dayEvents.length === 0;
          return (
            <div
              key={key}
              className={[
                css.calendarDay,
                outsideMonth ? css.calendarDayMuted : '',
                isEmpty ? css.calendarDayEmpty : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={css.calendarDate}>
                <span className={css.calendarDateDayOnly}>{eventCalendarDayLabel(day)}</span>
                <span className={css.calendarDateWithMonth}>
                  {eventCalendarDayLabel(day, { includeMonth: true })}
                </span>
              </span>
              {dayEvents.map(ev => {
                const hasImage = Boolean(showImages && ev.imageUrl);
                return (
                  <a
                    key={ev.listingId || ev.path}
                    className={`${css.calendarEvent}${hasImage ? '' : ` ${css.calendarEventTextOnly}`}`}
                    href={ev.path || '#'}
                    title={eventCalendarCellLabel(ev)}
                  >
                    {hasImage ? <img src={ev.imageUrl} alt="" loading="lazy" /> : null}
                    <span>{eventCalendarCellLabel(ev)}</span>
                  </a>
                );
              })}
              {/* Desktop only (CSS hides on mobile) — mobile expands to show every showing. */}
              {dayEvents.length > 3 ? (
                moreEventsHref ? (
                  <a
                    className={css.calendarMore}
                    href={moreEventsHref}
                    aria-label={`See ${dayEvents.length - 3} more events in the full list`}
                  >
                    +{dayEvents.length - 3} more
                  </a>
                ) : (
                  <span className={css.calendarMore}>+{dayEvents.length - 3} more</span>
                )
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

SeoEventCalendar.propTypes = {
  events: array,
  venueName: string,
  showImages: bool,
  /** In-page hash (or URL) for “+N more”; null/empty disables the link. */
  moreEventsHref: string,
};

export default SeoEventCalendar;
